import { useEffect, useState } from 'react';

// The UM-DragonEye desktop app (Windows .exe). The header button and the Agents
// page both point here. The installer and its manifest live next to the launcher
// scripts under /agent/ (desktop/scripts/package-release.ps1 puts them there).
// To host the installer somewhere else (e.g. a GitHub release), set
// REACT_APP_DESKTOP_APP_URL; the download is then offered without a manifest.
const CONFIGURED_URL = process.env.REACT_APP_DESKTOP_APP_URL || '';

// Where the installers and their manifests live. Set REACT_APP_DESKTOP_APP_BASE_URL (e.g.
// https://downloads.example.com) to serve all of them from one host, such as a Cloudflare R2 bucket.
// The version and SHA-256 details keep working because the manifests are read from the same place,
// so that host must allow this site to read them (CORS). The per-file URL settings still override it.
const BASE = (process.env.REACT_APP_DESKTOP_APP_BASE_URL || '/agent').replace(/\/+$/, '');

// Same origin as the manifest check below, so the button only ever points at a file this site serves
// (a hard-coded production host sent local and staging users to a login page).
export const APP_DOWNLOAD_URL = CONFIGURED_URL || `${BASE}/UM-DragonEye-Setup.exe`;

export const APP_REQUIREMENTS = 'Windows 10 or later · 64-bit';

// The Linux build (desktop/scripts/package-release.sh): an AppImage that runs anywhere, and a .deb for
// Debian/Ubuntu. Same rules as Windows: files next to the launcher scripts, or REACT_APP_DESKTOP_APP_LINUX_URL
// (and optionally REACT_APP_DESKTOP_APP_LINUX_DEB_URL) when they are hosted elsewhere, e.g. a GitHub release.
const LINUX_CONFIGURED_URL = process.env.REACT_APP_DESKTOP_APP_LINUX_URL || '';
export const LINUX_APPIMAGE_URL = LINUX_CONFIGURED_URL || `${BASE}/UM-DragonEye-Linux.AppImage`;
export const LINUX_DEB_URL = process.env.REACT_APP_DESKTOP_APP_LINUX_DEB_URL
  || (LINUX_CONFIGURED_URL ? '' : `${BASE}/UM-DragonEye-Linux.deb`);
// Arch Linux: package-release.sh publishes a PKGBUILD next to the .deb that repackages it for pacman.
export const LINUX_PKGBUILD_URL = `${BASE}/PKGBUILD`;
export const LINUX_REQUIREMENTS = 'Linux · 64-bit · AppImage or .deb';

// True for desktop Linux (Android's user agent also says Linux).
export function isLinuxClient() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  return /Linux/i.test(ua) && !/Android/i.test(ua);
}

const MANIFEST_URL = `${BASE}/desktop-app.json`;
const LINUX_MANIFEST_URL = `${BASE}/desktop-app-linux.json`;

// What /agent/desktop-app.json must contain, or the download is not offered.
export function parseManifest(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const { version, released, size_bytes: bytes, sha256, notes } = raw;
  if (typeof version !== 'string' || !version) return null;
  if (typeof sha256 !== 'string' || !/^[0-9a-f]{64}$/i.test(sha256)) return null;
  if (typeof bytes !== 'number' || !(bytes > 0)) return null;
  return {
    version,
    released: typeof released === 'string' ? released : '',
    size: `${(bytes / 1048576).toFixed(1)} MB`,
    sha256: sha256.toLowerCase(),
    notes: Array.isArray(notes)
      ? notes.filter((n) => typeof n === 'string' && n.trim()).slice(0, 6)
      : [],
  };
}

// What /agent/desktop-app-linux.json must contain: a version plus size and SHA-256 for each file.
export function parseLinuxManifest(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const { version, released, notes } = raw;
  if (typeof version !== 'string' || !version) return null;
  const file = (f) => {
    if (!f || typeof f !== 'object') return null;
    if (typeof f.sha256 !== 'string' || !/^[0-9a-f]{64}$/i.test(f.sha256)) return null;
    if (typeof f.size_bytes !== 'number' || !(f.size_bytes > 0)) return null;
    return { size: `${(f.size_bytes / 1048576).toFixed(1)} MB`, sha256: f.sha256.toLowerCase() };
  };
  const appimage = file(raw.appimage);
  const deb = file(raw.deb);
  if (!appimage && !deb) return null;
  return {
    version,
    released: typeof released === 'string' ? released : '',
    appimage,
    deb,
    notes: Array.isArray(notes)
      ? notes.filter((n) => typeof n === 'string' && n.trim()).slice(0, 6)
      : [],
  };
}

let cache; // one fetch per page load, shared by the header and the Agents page
let linuxCache;

function loadManifest() {
  if (!cache) {
    cache = fetch(MANIFEST_URL, { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : null))
      .then(parseManifest)
      .catch(() => null);
  }
  return cache;
}

function loadLinuxManifest() {
  if (!linuxCache) {
    linuxCache = fetch(LINUX_MANIFEST_URL, { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : null))
      .then(parseLinuxManifest)
      .catch(() => null);
  }
  return linuxCache;
}

/**
 * Whether to offer the app download, and its details. Nothing is offered until a
 * release has been published (a valid manifest) or a download URL is configured,
 * so the site never shows a button that leads to a 404.
 */
export function useDesktopApp() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let alive = true;
    loadManifest().then((m) => alive && setInfo(m));
    return () => {
      alive = false;
    };
  }, []);

  return { available: info !== null || Boolean(CONFIGURED_URL), info };
}

/** The same for the Linux build: offered once its manifest is published or a Linux URL is configured. */
export function useLinuxDesktopApp() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let alive = true;
    loadLinuxManifest().then((m) => alive && setInfo(m));
    return () => {
      alive = false;
    };
  }, []);

  return { available: info !== null || Boolean(LINUX_CONFIGURED_URL), info };
}
